// Tapestry WS client — v7 hybrid (#I28 · #API58).
//
// Spec: .meshkore/docs/architecture/tapestry-algorithm-v7.md (IMMUTABLE).
//
// Server owns the 60 s snapshot in D1 (read by /pieces on cold-load and by
// the welcome frame). Tick frames every 2 s carry POINTS ONLY — no
// position deltas. The browser's `runTick` in App.tsx computes movement
// from the points feed using the same algorithm the server simulates
// internally for its snapshot.
//
// Wire format (matches apps/api/src/tapestry.rs):
//   welcome (first frame): { proto: "v2", ts, zone, pieces: [{piece_id, likes, x, y}] }
//   tick frame:            { ts, changes: [{piece_id, likes}] }   // POINTS ONLY
//
// Backward-compat: tick frames MAY still surface `x`/`y` fields if rolling
// back to a v6 server; the App.tsx handler ignores them in v7. The
// handshake requires `?proto=v2` in the URL — older clients without that
// query string are rejected with HTTP 426.

import { apiBase } from "./api";

export type ZoneKey = "nucleus" | "oort";

export interface CellChange {
  piece_id: string;
  likes?: number;
  x?: number;
  y?: number;
  /** i35 optimistic publish — present on "piece_added" frames so the
   *  client can paint a new piece without waiting for `/pieces`. */
  image_url?: string;
  /** "added" on optimistic-publish frames; absent on tick deltas. */
  kind?: "added";
}

export interface SnapshotEntry {
  piece_id: string;
  likes: number;
  x: number;
  y: number;
}

export type TapestryMessage =
  | { kind: "welcome"; zone: ZoneKey; ts: number; pieces: SnapshotEntry[] }
  | { kind: "frame"; zone: ZoneKey; ts: number; changes: CellChange[] };

export type TapestryHandler = (msg: TapestryMessage) => void;

const ZONES: ZoneKey[] = ["nucleus", "oort"];

function wsBaseUrl(): string {
  const base = apiBase || (typeof window !== "undefined" ? window.location.origin : "");
  if (!base) return "";
  return base.replace(/^http/, "ws");
}

/** Open one WS per zone. Returns a single closer that tears them all down. */
export function openTapestryStream(onMessage: TapestryHandler): () => void {
  const wsBase = wsBaseUrl();
  if (!wsBase) {
    return () => {
      /* no-op */
    };
  }
  const closers = ZONES.map((zone) => openZoneStream(wsBase, zone, onMessage));
  return () => {
    for (const c of closers) c();
  };
}

function openZoneStream(
  wsBase: string,
  zone: ZoneKey,
  onMessage: TapestryHandler,
): () => void {
  let socket: WebSocket | null = null;
  let closedByCaller = false;
  let firstFrame = true;
  let backoffMs = 1000;

  const connect = () => {
    if (closedByCaller) return;
    firstFrame = true;
    try {
      socket = new WebSocket(`${wsBase}/tapestry/stream?zone=${zone}&proto=v2`);
    } catch {
      scheduleReconnect();
      return;
    }
    socket.onopen = () => {
      backoffMs = 1000;
    };
    socket.onmessage = (ev) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(ev.data as string);
      } catch {
        return;
      }
      if (!parsed || typeof parsed !== "object") return;
      const obj = parsed as Record<string, unknown>;
      // Welcome frame: distinguished by the presence of `pieces` (initial
      // snapshot). After that, all frames are tick deltas with `changes`.
      if (firstFrame) {
        firstFrame = false;
        if (Array.isArray(obj.pieces)) {
          onMessage({
            kind: "welcome",
            zone,
            ts: typeof obj.ts === "number" ? obj.ts : 0,
            pieces: obj.pieces as SnapshotEntry[],
          });
          return;
        }
        // No snapshot? Still treat the rest as frames below.
      }
      if (Array.isArray(obj.changes)) {
        onMessage({
          kind: "frame",
          zone,
          ts: typeof obj.ts === "number" ? obj.ts : 0,
          changes: obj.changes as CellChange[],
        });
      }
    };
    socket.onerror = () => {
      /* fall through to onclose */
    };
    socket.onclose = () => {
      if (closedByCaller) return;
      scheduleReconnect();
    };
  };

  const scheduleReconnect = () => {
    const delay = Math.min(backoffMs, 15_000);
    backoffMs = Math.min(backoffMs * 2, 30_000);
    setTimeout(connect, delay);
  };

  connect();

  return () => {
    closedByCaller = true;
    socket?.close();
  };
}
